import { ChangeEvent, useState } from "react";
import { DateTime } from "luxon";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useTranslation } from "react-i18next";
import { SwitchGearConstants } from "../shared/constants/SwitchGearConstants";
import { FormContainer } from "../shared/FormLayout/FormContainer";
import { FormRow } from "../shared/FormLayout/FormRow";
import { assetTypeFieldMapping, SurveyableAsset } from "../shared/interfaces/SurveyableAsset";
import { InternationalDatepicker } from "../shared/InternationalDatepicker";
import { AssetTypeKeys } from "./models/AssetTypeKeys";
import { getAssetTypeLabel } from "../shared/constants/managedSurveyAssetTypes";

interface Props {
  switchGear: SurveyableAsset;
  onChange: (switchgear: SurveyableAsset) => void;
}

const insulationOptions: Record<AssetTypeKeys, string[]> = {
  [AssetTypeKeys.GisSwitchgear]: ["$INSU_SF6", "$INSU_SF6_FREE"],
  [AssetTypeKeys.OutdoorSwitchyard]: ["$INSU_AIR"],
  [AssetTypeKeys.Switchgear]: [],
  [AssetTypeKeys.SwitchgearSimple]: [],
  [AssetTypeKeys.Transformer]: [],
};

const OTHER_OPTION = "OTHER";
const predefinedOperatingVoltages = SwitchGearConstants.operatingVoltages.map((voltage) => voltage.toString());

type OperatingVoltageOption = {
  label: string;
  value: string;
};

export function SwitchgearForm(props: Props) {
  const { onChange } = props;
  const { t } = useTranslation();

  const [isOtherOperatingVoltageSelected, setIsOtherOperatingVoltageSelected] = useState(false);

  const operatingVoltageOptions: OperatingVoltageOption[] = [
    ...SwitchGearConstants.operatingVoltages.map((voltage) => ({
      label: voltage.toString(),
      value: voltage.toString(),
    })),
    {
      label: t("common.other"),
      value: OTHER_OPTION,
    },
  ];

  const insulationOptionsTranslated = (assetType: AssetTypeKeys) => {
    const options = insulationOptions[assetType];
    const keys = options && options.length > 0 ? options : SwitchGearConstants.insulationTypeKeys;

    return keys.map((key) => ({
      label: t(`UTP2.insulationType.${key}`),
      value: key,
    }));
  };

  const assetType = getAssetTypeLabel(props.switchGear.assetType) as AssetTypeKeys;
  const fieldsToRender = assetTypeFieldMapping[assetType] || [];

  const isCustomOperatingVoltage =
    props.switchGear.operatingVoltage != null &&
    props.switchGear.operatingVoltage !== "" &&
    !predefinedOperatingVoltages.includes(props.switchGear.operatingVoltage);

  const operatingVoltageDropdownValue =
    isOtherOperatingVoltageSelected || isCustomOperatingVoltage ? OTHER_OPTION : props.switchGear.operatingVoltage;

  function handleTypeChange(event: { target: { value: string } }) {
    const value = event.target.value;
    onChange({ ...props.switchGear, switchGearType: value });
  }

  function handleRatedChange(event: { target: { value: string } }) {
    const value = event.target.value;
    onChange({ ...props.switchGear, ratedVoltage: value });
  }

  function handleOperatingChange(event: { value: string }) {
    const value = event.value;

    if (value === OTHER_OPTION) {
      setIsOtherOperatingVoltageSelected(true);
      onChange({
        ...props.switchGear,
        operatingVoltage: isCustomOperatingVoltage ? props.switchGear.operatingVoltage : "",
      });
      return;
    }

    setIsOtherOperatingVoltageSelected(false);
    onChange({ ...props.switchGear, operatingVoltage: value });
  }

  function handleCustomOperatingVoltageChange(event: ChangeEvent<HTMLInputElement>) {
    const value = event.target.value.trim();

    if (value === "") {
      onChange({ ...props.switchGear, operatingVoltage: "" });
      return;
    }

    if (!/^\d*\.?\d*$/.test(value)) {
      return;
    }

    onChange({ ...props.switchGear, operatingVoltage: value });
  }

  function handleManufacturerChange(event: { target: { value: string } }) {
    const value = event.target.value;
    onChange({ ...props.switchGear, switchGearManufacturer: value });
  }

  function handleInsulationChange(event: { target: { value: string } }) {
    const value = event.target.value;
    onChange({ ...props.switchGear, insulation: value });
  }

  function handleInstallDateChange(event: { value: Date | Date[] | null | undefined }) {
    const value = event.value;

    if (!(value instanceof Date)) {
      onChange({ ...props.switchGear, installDate: undefined });
      return;
    }

    const isoDate = DateTime.fromJSDate(value).toISODate();
    onChange({ ...props.switchGear, installDate: isoDate ?? undefined });
  }

  return (
    <FormContainer>
      {fieldsToRender.includes("manufacturer") && (
        <FormRow
          label={t("switchgear.manufacturer")}
          value={
            <Dropdown
              value={props.switchGear.switchGearManufacturer}
              options={SwitchGearConstants.manufacturers}
              placeholder={t("switchgear.manufacturer")}
              onChange={handleManufacturerChange}
            />
          }
        />
      )}
      {fieldsToRender.includes("type") && (
        <FormRow
          label={t("switchgear.type")}
          value={
            <Dropdown
              value={props.switchGear.switchGearType ?? props.switchGear.assetType}
              options={SwitchGearConstants.switchGearTypesByManufacturer(props.switchGear.switchGearManufacturer)}
              placeholder={t("switchgear.type")}
              onChange={handleTypeChange}
            />
          }
        />
      )}
      {fieldsToRender.includes("ratedVoltage") && (
        <FormRow
          label={t("switchgear.ratedVoltage")}
          value={
            <Dropdown
              value={props.switchGear.ratedVoltage}
              options={SwitchGearConstants.ratedVoltages.map((v) => v.toString())}
              placeholder={t("switchgear.ratedVoltage")}
              onChange={handleRatedChange}
            />
          }
        />
      )}
      {fieldsToRender.includes("operatingVoltage") && (
        <FormRow
          label={t("switchgear.operatingVoltage")}
          value={
            <div className="flex gap-2">
              <Dropdown
                value={operatingVoltageDropdownValue}
                options={operatingVoltageOptions}
                optionLabel="label"
                optionValue="value"
                placeholder={t("switchgear.operatingVoltage")}
                onChange={handleOperatingChange}
              />
              {operatingVoltageDropdownValue === OTHER_OPTION && (
                <InputText
                  value={props.switchGear.operatingVoltage ?? ""}
                  onChange={handleCustomOperatingVoltageChange}
                  placeholder={t("switchgear.enterOperatingVoltage")}
                  inputMode="decimal"
                />
              )}
            </div>
          }
        />
      )}
      {fieldsToRender.includes("insulation") && (
        <FormRow
          label={t("switchgear.insulation")}
          value={
            <Dropdown
              value={props.switchGear.insulation}
              options={insulationOptionsTranslated(assetType)}
              placeholder={t("switchgear.insulation")}
              onChange={handleInsulationChange}
            />
          }
        />
      )}
      {fieldsToRender.includes("installDate") && (
        <FormRow
          label={t("switchgear.installDate")}
          value={
            <InternationalDatepicker
              value={props.switchGear.installDate ? new Date(props.switchGear.installDate) : undefined}
              onChange={handleInstallDateChange}
              showIcon
            />
          }
        />
      )}
    </FormContainer>
  );
}

export default SwitchgearForm;
